export type MfaEnrollment = {
  secret: string;
  otpauthUri: string;
  expiresAt: number;
};

// Enrollment material stays in component memory. Never log or persist this value.
export function readMfaEnrollment(
  value: unknown,
  email: string,
  requestStartedAt: number,
): MfaEnrollment {
  const invalid = () =>
    new Error(
      "Authenticator setup could not be displayed safely. Start a new setup.",
    );
  if (!value || typeof value !== "object") throw invalid();
  const data = value as Record<string, unknown>;
  if (
    typeof data.secret !== "string" ||
    !/^[A-Z2-7]{32}$/.test(data.secret) ||
    typeof data.otpauthUri !== "string" ||
    data.otpauthUri.length > 2048 ||
    typeof data.expiresInSeconds !== "number" ||
    !Number.isInteger(data.expiresInSeconds) ||
    data.expiresInSeconds < 1 ||
    data.expiresInSeconds > 600 ||
    !email ||
    !Number.isFinite(requestStartedAt)
  )
    throw invalid();
  try {
    const uri = new URL(data.otpauthUri);
    const expected = {
      secret: data.secret,
      issuer: "ZentroTax",
      algorithm: "SHA1",
      digits: "6",
      period: "30",
    };
    if (
      uri.protocol !== "otpauth:" ||
      uri.hostname !== "totp" ||
      uri.port ||
      uri.username ||
      uri.password ||
      uri.hash ||
      decodeURIComponent(uri.pathname.slice(1)) !== `ZentroTax:${email}` ||
      Array.from(uri.searchParams).length !== Object.keys(expected).length ||
      Object.entries(expected).some(
        ([key, expectedValue]) =>
          uri.searchParams.getAll(key).length !== 1 ||
          uri.searchParams.get(key) !== expectedValue,
      )
    )
      throw invalid();
  } catch {
    throw invalid();
  }
  return {
    secret: data.secret,
    otpauthUri: data.otpauthUri,
    // Conservatively start at request dispatch; the server is authoritative.
    expiresAt: requestStartedAt + data.expiresInSeconds * 1000,
  };
}

export function mfaSetupError(code: string, email: string): string {
  switch (code) {
    case "INVALID_MFA_CODE":
      return `This code does not match the current setup for ${email}. In your authenticator app, use the entry added from this screen, not another account's entry. Turn on automatic date and time on your phone, then enter its current 6-digit code.`;
    case "MFA_ENROLLMENT_EXPIRED":
      return "This setup expired. Enter this account's password and choose Set up authenticator again. Scan the new QR code; the earlier setup entry will no longer work.";
    case "MFA_ALREADY_ENABLED":
      return "An authenticator is already enabled for this account. Reload this page to check its current status; do not start another setup.";
    case "INVALID_CREDENTIALS":
    case "REAUTHENTICATION_REQUIRED":
      return "Account verification was not accepted. Check this account's current password. If you have made repeated attempts, wait before trying again; the account may be temporarily locked.";
    case "REAUTHENTICATION_LOCKED":
      return "Account verification is temporarily locked after repeated attempts. Wait before trying again; do not keep submitting codes.";
    case "RATE_LIMITED":
    case "RATE_LIMIT_CAPACITY":
      return "Too many security requests. Wait before trying again. Your authenticator requirement has not been changed.";
    case "AUTHENTICATION_REQUIRED":
      return "Your session has ended. Sign in again before setting up this account's authenticator.";
    default:
      return "Authenticator setup could not be completed. Check your connection and reload to check account status before starting again. No code will be resubmitted automatically.";
  }
}
